import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '../utils';

const tipArrowStyles = 'fill-foreground z-50';
function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function TooltipRoot({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    className={cn(tipArrowStyles, className)}
    {...props}
  />
));

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  container,
  trigger,
  showArrow = true,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  container?: React.ComponentProps<typeof TooltipPrimitive.Portal>['container'];
  /** @internal Used to add role="status" for click-triggered tooltips */
  trigger?: 'hover' | 'click';
  showArrow?: boolean;
}) {
  return (
    <TooltipPrimitive.Portal container={container}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit rounded-md px-3 py-1.5 text-xs text-balance',
          'origin-(--radix-tooltip-content-transform-origin)',
          className,
        )}
        {...props}
        // role "status" creates a live region which announces content to screen readers on open
        // @see https://inclusive-components.design/tooltips-toggletips/
        {...(trigger === 'click' && { role: 'status' })}
      >
        {children}
        {showArrow && <TooltipArrow />}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

function TooltipContentCard({
  className,
  sideOffset = 4,
  children,
  container,
  trigger,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  container?: React.ComponentProps<typeof TooltipPrimitive.Portal>['container'];
  /** @internal Used to add role="status" for click-triggered tooltips */
  trigger?: 'hover' | 'click';
}) {
  return (
    <TooltipPrimitive.Portal container={container}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content-card"
        sideOffset={sideOffset}
        className={cn(
          'bg-white text-foreground border border-border shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit rounded-md px-3 py-2 text-sm',
          'origin-(--radix-tooltip-content-transform-origin)',
          className,
        )}
        {...props}
        {...(trigger === 'click' && { role: 'status' })}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

type TooltipProps = React.PropsWithChildren<
  {
    content?: string | null | React.ReactNode;
    asChild?: boolean;
    /**
     * How the tooltip is triggered:
     * - "hover": Shows on mouse hover (default)
     * - "click": Shows on click/tap (ideal for touch devices)
     */
    trigger?: 'hover' | 'click';
    /**
     * When true, the tooltip is always visible (only works with trigger="hover")
     */
    alwaysOpen?: boolean;
  } & Omit<
    React.ComponentPropsWithoutRef<typeof TooltipContent>,
    'content' | 'children' | 'clickMode'
  >
>;

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  asChild,
  container,
  alwaysOpen,
  trigger = 'hover',
  ...props
}) => {
  const [isOpen, setIsOpen] = React.useState(alwaysOpen ?? false);

  if (!content) {
    return <>{children}</>;
  }

  if (trigger === 'click') {
    return (
      <TooltipRoot
        open={isOpen}
        onOpenChange={(open) => {
          // Only allow closing via onOpenChange (click outside, Escape key)
          // Opening is handled exclusively by the click handler
          if (!open) setIsOpen(false);
        }}
      >
        <TooltipTrigger
          asChild={asChild}
          type="button"
          className="cursor-pointer"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent container={container} trigger="click" {...props}>
          {content}
        </TooltipContent>
      </TooltipRoot>
    );
  }

  return (
    <TooltipRoot delayDuration={300} open={alwaysOpen}>
      <TooltipTrigger asChild={asChild} type="button">
        {children}
      </TooltipTrigger>
      <TooltipContent container={container} {...props}>
        {content}
      </TooltipContent>
    </TooltipRoot>
  );
};

export {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipContentCard,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
};
export type { TooltipProps };
